import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ContentCut, SubdirectoryArrowRight } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Autocomplete } from '@mui/material'
import { Button, useGetList, useNotify, useTranslate } from 'react-admin'

import { IconCirclePlus } from '@tabler/icons-react'
import { useImmer } from 'use-immer'
import {
  ArtworkApiDTO,
  PostApiArtworksCurrentArtworkIdDissociateApiBodies,
} from '../../codegen/generated/Api.schemas'
import { LXYoutubeLink } from '../../components/LXYoutubeLink'
import { API } from '../../helpers/Api.helper'
import { ArtworkDissociateApiDTO, PeriodsItem } from '../../types/Artwork'
import { Beneficiary } from '../../types/Beneficiary'
import { Product } from '../../types/Product'
import { YoutubeAsset } from '../../types/YoutubeAsset'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import genericStyles from './../../generics.module.scss'
import { ArtworkDissociationData } from './ArtworkDissociationData'
import { ArtworksCreateEmbed } from './ArtworksCreateEmbed'

export interface ArtworksDissociateAssetModalProps {
  artworkId: ArtworkApiDTO['id']
  assetId: YoutubeAsset['id']
  beneficiaryId: Beneficiary['id']
  cms: YoutubeChannel['cms']
  productId: Product['id']
  onSuccess?: () => void
}

export const ArtworksDissociateAssetModal: React.FC<ArtworksDissociateAssetModalProps> = ({
  artworkId,
  assetId,
  beneficiaryId,
  cms,
  productId,
  onSuccess,
}) => {
  const translate = useTranslate()
  const notify = useNotify()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDissociating, setIsDissociating] = useState(false)

  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary>()
  const [selectedProduct, setSelectedProduct] = useState<Product>()
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkApiDTO>()
  const [dissociatedData, setDissociatedData] = useState<ArtworkDissociateApiDTO>()

  const [selectedPeriods, setSelectedPeriods] = useImmer<PeriodsItem[]>([])
  const [isCreatingArtwork, setIsCreatingArtwork] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [error, setError] = useState<string>()

  const beneficiariesQuery = useGetList<Beneficiary>(
    'beneficiaries',
    {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 1000 },
    },
    {
      enabled: isModalOpen,
    },
  )
  const productsQuery = useGetList<Product>(
    'products',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        beneficiaryId: selectedBeneficiary?.id,
      },
    },
    {
      enabled: !!selectedBeneficiary,
    },
  )
  const artworksQuery = useGetList<ArtworkApiDTO>(
    'artworks',
    {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 999 },
      filter: { productId: selectedProduct?.id },
    },
    {
      enabled: !!selectedProduct,
    },
  )

  const defaultBeneficiary = useMemo(
    () => (beneficiariesQuery.data ?? []).find(b => b.id === beneficiaryId),
    [beneficiariesQuery.data, beneficiaryId],
  )
  const defaultProduct = useMemo(
    () => (productsQuery.data ?? []).find(b => b.id === productId),
    [productsQuery.data, productId],
  )

  const artworks = useMemo(
    () => (artworksQuery.data ?? []).filter(a => a.id !== artworkId),
    [artworksQuery.data, artworkId],
  )

  const closeModal = useCallback(() => {
    onSuccess?.()
    setIsModalOpen(false)
    setIsCreatingArtwork(false)
    setError(undefined)
  }, [onSuccess])

  const dissociateArtworksDryRun = useCallback(async () => {
    setError(undefined)
    setIsLoadingData(true)
    try {
      if (selectedArtwork) {
        const response = await API.post<any, ArtworkDissociateApiDTO>(
          `/api/artworks/${artworkId}/dissociateDryRun`,
          {
            assetId,
            targetArtworkId: selectedArtwork.id,
          },
        )
        setDissociatedData(response.data)
        setIsLoadingData(false)
      }
    } catch (e: any) {
      const message = e.response?.data?.message || e.response?.data

      notify(`${message}`, { type: 'error' })
      console.error('Error - dissociate artworks', e)
      setError(`${message} - ${e.response.data.content?.fieldError ?? e.response.data.content}`)
      setIsDissociating(false)
      setIsLoadingData(false)
    }
  }, [assetId, selectedArtwork, artworkId, notify])

  const dissociateArtworks = useCallback(async () => {
    setIsDissociating(true)
    setError(undefined)
    try {
      if (dissociatedData && selectedArtwork) {
        const periods = selectedPeriods.map(p => ({
          month: p.month,
          year: p.year,
        }))
        await API.post<PostApiArtworksCurrentArtworkIdDissociateApiBodies>(
          `/api/artworks/${artworkId}/dissociate`,
          {
            assetId,
            dryRunHash: dissociatedData.hash,
            targetArtworkId: selectedArtwork?.id,
            periods,
          },
        )
        closeModal()
        setIsDissociating(false)
      }
    } catch (e: any) {
      const message = e.response?.data?.message || e.response?.data

      notify(`${message}`, { type: 'error' })
      console.error('Error - dissociate artworks', e)
      setError(`${message} - ${e.response.data.content?.fieldError ?? e.response.data.content}`)
      setIsDissociating(false)
    }
  }, [assetId, artworkId, closeModal, notify, dissociatedData, selectedPeriods, selectedArtwork])

  const onCreateArtwork = useCallback(
    async (artwork: ArtworkApiDTO) => {
      setIsCreatingArtwork(false)
      setSelectedArtwork(artwork)
      await artworksQuery.refetch()
    },
    [artworksQuery.refetch],
  )

  const reset = () => {
    setDissociatedData(undefined)
    setIsLoadingData(false)
    setError(undefined)
  }

  useEffect(() => {
    if (selectedArtwork) {
      dissociateArtworksDryRun()
    }
  }, [selectedArtwork, dissociateArtworksDryRun])

  useEffect(() => {
    if (defaultBeneficiary) {
      setSelectedBeneficiary(defaultBeneficiary)
    }
  }, [defaultBeneficiary])

  useEffect(() => {
    if (defaultProduct) {
      setSelectedProduct(defaultProduct)
    }
  }, [defaultProduct])

  return (
    <>
      <Button
        size="small"
        color="primary"
        onClick={() => setIsModalOpen(true)}
        label={translate('_generics.dissociate')}
        startIcon={
          <ContentCut
            color="primary"
            fontSize="small"
            style={{
              marginLeft: '0.5rem',
            }}
          />
        }
        style={{
          minWidth: 'fit-content',
        }}
      />
      <Dialog
        aria-labelledby="youtube-dissociate-modal"
        fullWidth
        maxWidth="lg"
        open={isModalOpen}
        onClose={() => closeModal()}
      >
        <DialogTitle id="youtube-dissociate-modal">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            {translate('pages.artworks.dissociateArtworks.title')}
            <LXYoutubeLink youtubeAssetId={assetId} cms={cms} />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {beneficiariesQuery.data ? (
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textPrimary">
                  {translate('pages.artworks.dissociateArtworks.description', {
                    assetId,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={6} spacing={6}>
                <InputLabel id="beneficiaries-list-dissociate" style={{ fontSize: '80%' }}>
                  {translate('_generics.beneficiary')}
                </InputLabel>
                <Autocomplete
                  id="beneficiaries-list-dissociate"
                  defaultValue={defaultBeneficiary}
                  options={beneficiariesQuery.data ?? []}
                  getOptionLabel={option => option.name}
                  renderInput={params => <TextField {...params} variant="standard" />}
                  renderOption={(props, option, index) => (
                    <li {...props} key={`beneficiary-${index}-${option.id}`}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText={translate('_generics.noResult')}
                  onChange={(_, value) => {
                    setSelectedBeneficiary(value ?? undefined)
                    setSelectedProduct(undefined)
                    setSelectedArtwork(undefined)
                    reset()
                  }}
                  disabled={isDissociating}
                  classes={{
                    tag: genericStyles.tagRemove,
                  }}
                />
                {typeof selectedBeneficiary !== 'undefined' && productsQuery.data && (
                  <Stack flexDirection="row" alignItems="center" gap={2} marginTop={2}>
                    <SubdirectoryArrowRight />
                    <Box sx={{ flex: 1 }}>
                      <InputLabel id="products-list-dissociate" style={{ fontSize: '80%' }}>
                        {translate('pages.products._title')}
                      </InputLabel>
                      <Autocomplete
                        id="products-list-dissociate"
                        defaultValue={defaultProduct}
                        options={productsQuery.data ?? []}
                        getOptionLabel={option => option.name}
                        renderInput={params => <TextField {...params} variant="standard" />}
                        renderOption={(props, option, index) => (
                          <li {...props} key={`artwork-${index}-${option.id}`}>
                            {option.name}
                          </li>
                        )}
                        noOptionsText={translate('_generics.noResult')}
                        onChange={(_, value) => {
                          setSelectedProduct(value ?? undefined)
                          setSelectedArtwork(undefined)
                          reset()
                        }}
                        disabled={isDissociating || !selectedBeneficiary}
                        classes={{
                          tag: genericStyles.tagRemove,
                        }}
                      />
                    </Box>
                  </Stack>
                )}
                {typeof selectedProduct !== 'undefined' && artworksQuery.data && (
                  <Stack flexDirection="row" alignItems="center" gap={2} marginTop={2}>
                    <SubdirectoryArrowRight />
                    <Box sx={{ flex: 1 }}>
                      <InputLabel id="artworks-list-dissociate" style={{ fontSize: '80%' }}>
                        {translate('pages.artworks._title')}
                      </InputLabel>
                      <Autocomplete
                        id="artworks-list-dissociate"
                        options={artworks}
                        getOptionLabel={option => option.name}
                        renderInput={params => <TextField {...params} variant="standard" />}
                        renderOption={(props, option, index) => (
                          <li {...props} key={`artwork-${index}-${option.id}`}>
                            {option.name}
                          </li>
                        )}
                        defaultValue={selectedArtwork}
                        value={selectedArtwork}
                        placeholder={translate('pages.artworks.placeholders.selectArtworks')}
                        noOptionsText={translate('_generics.noResult')}
                        onChange={(_, value) => {
                          setSelectedArtwork(value ?? undefined)
                          reset()
                        }}
                        disabled={
                          isDissociating ||
                          !selectedProduct ||
                          isCreatingArtwork ||
                          artworksQuery.isLoading
                        }
                        classes={{
                          tag: genericStyles.tagRemove,
                        }}
                        loading={artworksQuery.isLoading}
                      />
                    </Box>
                    <Tooltip
                      title={translate(
                        'pages.artworks.dissociateArtworks.createArtwork.createForProduct',
                        {
                          productName: selectedProduct.name,
                        },
                      )}
                      placement="top"
                      arrow
                    >
                      <IconButton
                        color="primary"
                        onClick={() => setIsCreatingArtwork(true)}
                        disabled={isCreatingArtwork}
                        sx={{
                          marginTop: '0.5rem',
                        }}
                      >
                        <IconCirclePlus />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Grid>
              {isCreatingArtwork && (
                <Grid item xs={6}>
                  <ArtworksCreateEmbed
                    beneficiaryId={selectedBeneficiary?.id ?? ''}
                    productId={selectedProduct?.id ?? ''}
                    onCreate={a => onCreateArtwork(a)}
                  />
                </Grid>
              )}
            </Grid>
          ) : (
            <></>
          )}
          {dissociatedData && (
            <Box mt={2}>
              <Divider
                component="div"
                style={{
                  marginTop: '3rem',
                }}
              />
              <ArtworkDissociationData
                data={dissociatedData}
                selectedPeriods={selectedPeriods}
                onSelect={sp => {
                  setSelectedPeriods(_selectedPeriods => {
                    const pIndex = _selectedPeriods.findIndex(
                      _sp => _sp.month === sp.month && _sp.year === sp.year,
                    )

                    if (pIndex >= 0) {
                      _selectedPeriods.splice(pIndex, 1)
                    } else {
                      _selectedPeriods.push(sp)
                    }
                    return _selectedPeriods
                  })
                }}
              />
            </Box>
          )}
          {isLoadingData && (
            <Box textAlign="center" marginTop="2rem">
              <CircularProgress size="2rem" />
            </Box>
          )}
          {error && (
            <p className={genericStyles.error}>
              <i>{translate('_generics.errorOccured')}</i>:&nbsp;
              <strong>{error}</strong>
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsModalOpen(false)}
            label={translate('_generics.cancel')}
            disabled={isDissociating}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              dissociateArtworks()
            }}
            label={translate('_generics.dissociate')}
            disabled={
              isDissociating ||
              !selectedArtwork ||
              (Object.keys(dissociatedData?.changes ?? {}).length > 0 &&
                selectedPeriods.length === 0)
            }
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
